import { useState } from "react";
import { X, Clock, ExternalLink } from "lucide-react";
import { useSuggestions, useDismissSuggestion } from "@/hooks/use-suggestions";
import type { ActiveSuggestion } from "@/hooks/use-suggestions";

/**
 * Today's recommendations (BW18 PR-G).
 *
 * Up to 5 suggestion cards from the engine. Each has:
 * - Priority dot
 * - Title + body
 * - Optional one-tap action
 * - Citation chevron (expand to see literature backing)
 * - Dismiss (x) and Snooze (clock) buttons
 */
export function SuggestionsList() {
  const q = useSuggestions();
  if (q.isLoading) {
    return (
      <section style={cardStyle()}>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          Loading recommendations…
        </p>
      </section>
    );
  }
  const items = q.data ?? [];
  if (items.length === 0) {
    return null; // Nothing to surface; don't take up space.
  }

  return (
    <section
      aria-labelledby="today-recommendations-heading"
      style={cardStyle()}
    >
      <h3 id="today-recommendations-heading" style={titleStyle()}>
        Today's recommendations
      </h3>
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        {items.map((s) => (
          <SuggestionCard key={s.id} suggestion={s} />
        ))}
      </div>
    </section>
  );
}

function SuggestionCard({ suggestion }: { suggestion: ActiveSuggestion }) {
  const [expanded, setExpanded] = useState(false);
  const dismiss = useDismissSuggestion();

  return (
    <article
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
        display: "grid",
        gap: "var(--space-2)",
      }}
    >
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)" }}>
        <PriorityDot priority={suggestion.priority} />
        <h4
          style={{
            margin: 0,
            fontSize: "var(--type-body)",
            fontWeight: 500,
            color: "var(--fg)",
            flex: 1,
          }}
        >
          {suggestion.title}
        </h4>
        <button
          type="button"
          onClick={() =>
            dismiss.mutate({ rule_key: suggestion.rule_key, snooze_hours: 24 })
          }
          aria-label="Snooze for 24 hours"
          style={iconButtonStyle()}
          title="Snooze 24h"
        >
          <Clock size={14} strokeWidth={1.5} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => dismiss.mutate({ rule_key: suggestion.rule_key })}
          aria-label="Dismiss"
          style={iconButtonStyle()}
          title="Dismiss"
        >
          <X size={14} strokeWidth={1.5} aria-hidden />
        </button>
      </header>

      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)", lineHeight: 1.5 }}>
        {suggestion.body}
      </p>

      <footer
        style={{
          marginTop: "var(--space-1)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        {suggestion.action && (
          <a
            href={suggestion.action.href ?? "#"}
            style={{
              fontSize: "var(--type-meta)",
              color: "var(--accent)",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            {suggestion.action.label} →
          </a>
        )}
        {suggestion.citations && suggestion.citations.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
              textDecorationColor: "var(--hair-2)",
            }}
          >
            {expanded ? "Hide" : "Show"} {suggestion.citations.length} citation
            {suggestion.citations.length === 1 ? "" : "s"}
          </button>
        )}
      </footer>

      {expanded && suggestion.citations && (
        <ul style={{ margin: 0, paddingLeft: "var(--space-4)", display: "grid", gap: 4 }}>
          {suggestion.citations.map((c) => (
            <li key={c.url} style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--fg-2)" }}
              >
                {c.title} <ExternalLink size={11} style={{ display: "inline", verticalAlign: "middle" }} />
              </a>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function PriorityDot({ priority }: { priority: ActiveSuggestion["priority"] }) {
  const color =
    priority === "high"
      ? "var(--brick)"
      : priority === "medium"
        ? "var(--accent)"
        : "var(--fg-4)";
  return (
    <span
      aria-label={`${priority} priority`}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        flex: "0 0 auto",
        alignSelf: "center",
      }}
    />
  );
}

function cardStyle(): React.CSSProperties {
  return {
    background: "var(--bg-2)",
    border: "1px solid var(--hair)",
    borderRadius: "var(--radius-surface)",
    padding: "var(--space-5) var(--space-6)",
  };
}

function titleStyle(): React.CSSProperties {
  return {
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: "var(--type-body)",
    margin: "0 0 var(--space-4)",
    color: "var(--fg)",
  };
}

function iconButtonStyle(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "transparent",
    border: "1px solid var(--hair)",
    color: "var(--fg-3)",
    cursor: "pointer",
    padding: 0,
  };
}
